import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../global-components/src";
import { useMessage } from "../../../global-components/src/index";
import { BUCKET_READBOOKS_URL, PACOCA_API_URL, READBOOKS_API_URL } from "../../../global-components/src/utils/vars";
import { errorMessage } from "../../../global-components/src/utils/text";
import Spinner from "../../../global-components/src/components/spinner/spiner";
import { Clipboard, CreditCard, CheckCircle, Lock, X } from "react-feather";
import { BookOpen } from "lucide-react";
import { CatalogBook } from "../../../types";
import "./style.css";

const formatCurrency = (value: number, currency = "BRL") =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(value);

export default function BookPurchasePage() {
    const { uuid } = useParams();
    const { token, user } = useAuth();
    const { setMessage } = useMessage();
    const navigate = useNavigate();

    const [book, setBook] = useState<CatalogBook | null>(null);
    const [loadingBook, setLoadingBook] = useState(true);
    const [loading, setLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<"wallet" | "gerencianet_pix">("gerencianet_pix");
    const [walletBalance, setWalletBalance] = useState(0);
    const [loadingWallet, setLoadingWallet] = useState(true);

    // Estado do PIX
    const [qrCode, setQrCode] = useState<string | null>(() => sessionStorage.getItem(`qr-book-${uuid}`));
    const [copyPaste, setCopyPaste] = useState<string | null>(() => sessionStorage.getItem(`cp-book-${uuid}`));
    const [txid, setTxid] = useState<string | null>(() => sessionStorage.getItem(`txid-book-${uuid}`));
    const [copiado, setCopiado] = useState(false);
    const [loadingVerify, setLoadingVerify] = useState(false);
    const [loadingVerifyBg, setLoadingVerifyBg] = useState(false);
    const [paid, setPaid] = useState(false);

    // Persiste estado do PIX na session
    useEffect(() => {
        if (qrCode) sessionStorage.setItem(`qr-book-${uuid}`, qrCode);
        if (copyPaste) sessionStorage.setItem(`cp-book-${uuid}`, copyPaste);
        if (txid) sessionStorage.setItem(`txid-book-${uuid}`, txid);
    }, [qrCode, copyPaste, txid]);

    // Polling de verificação
    useEffect(() => {
        if (!txid || paid) return;
        const interval = setInterval(() => verifyPayment(false), 5000);
        return () => clearInterval(interval);
    }, [txid, paid]);

    useEffect(() => {
        fetchBook();
        // fetchWallet();
    }, [uuid]);

    const fetchBook = async () => {
        try {
            const res = await axios.get(`${READBOOKS_API_URL}/catalog/${uuid}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = res.data;
            setBook(data.book);
            // Já comprou → redireciona para leitura
            if (data.has_purchased || data.can_read) {
                navigate(`/livros/ler/${uuid}`);
            }
        } catch (err: any) {
            setMessage(errorMessage(err), "error");
        } finally {
            setLoadingBook(false);
        }
    };

    // const fetchWallet = async () => {
    //     try {
    //         const res = await axios.get(`${PACOCA_API_URL}/wallet`, {
    //             headers: { Authorization: `Bearer ${token}` },
    //         });
    //         setWalletBalance(Number(res.data.balance));
    //     } finally {
    //         setLoadingWallet(false);
    //     }
    // };

    const submit = async () => {
        if (loading || !book) return;
        setLoading(true);
        try {
            // { gateway: paymentMethod },
            const res = await axios.post(
                `${READBOOKS_API_URL}/catalog/${uuid}/purchase`,
                { gateway: "gerencianet_pix" },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // if (paymentMethod === "wallet") {
            //     setMessage("Compra realizada! Boa leitura!", "success");
            //     cancelPix();
            //     navigate(`/livros/ler/${uuid}`);
            //     return;
            // }

            if (res.data.qrcode) {
                setQrCode(res.data.qrcode);
                setCopyPaste(res.data.copy_paste);
                setTxid(res.data.txid);
            }
        } catch (err: any) {
            setMessage(errorMessage(err), "error");
        } finally {
            setLoading(false);
        }
    };

    const verifyPayment = async (manual = false) => {
        if (!txid) return;
        if (manual) setLoadingVerify(true);
        if (!manual && loadingVerifyBg) return;
        setLoadingVerifyBg(true);

        try {
            const res = await axios.post(
                `${READBOOKS_API_URL}/catalog/${uuid}/verify-payment`,
                { txid },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (res.data.status === "paid") {
                setPaid(true);
                setMessage("Pagamento confirmado! Boa leitura!", "success");
                cancelPix();
                navigate(`/livros/ler/${uuid}`);
            } else if (manual) {
                setMessage("Pagamento ainda não confirmado. Aguarde alguns instantes.", "warning");
            }
        } catch (err: any) {
            if (manual) setMessage(errorMessage(err), "error");
        } finally {
            setLoadingVerify(false);
            setLoadingVerifyBg(false);
        }
    };

    const cancelPix = () => {
        setQrCode(null);
        setCopyPaste(null);
        setTxid(null);
        sessionStorage.removeItem(`qr-book-${uuid}`);
        sessionStorage.removeItem(`cp-book-${uuid}`);
        sessionStorage.removeItem(`txid-book-${uuid}`);
    };

    const copyCode = async () => {
        if (!copyPaste) return;
        try {
            await navigator.clipboard.writeText(copyPaste);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2500);
        } catch {}
    };

    if (loadingBook) {
        return (
            <div className="purchase-loading">
                <Spinner size="36px" />
            </div>
        );
    }

    if (!book) return null;

    // ── Tela de PIX ──────────────────────────────────
    if (qrCode) {
        return (
            <div className="purchase-wrapper">
                <div className="purchase-card">
                    <div className="purchase-pix-header">
                        <div className="pix-icon-wrapper">
                            <img src="/img/pix-logo.svg" alt="PIX" className="pix-logo" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                            <span className="pix-fallback-text">PIX</span>
                        </div>
                        <h2>Pague via PIX</h2>
                        <p>Escaneie o QR Code ou use o código copia e cola</p>
                        <span className="purchase-price-badge">{formatCurrency(book.price, book.currency)}</span>
                    </div>

                    <div className="pix-book-info">
                        {book.cover_url && (
                            <img src={book.cover_url} alt={book.title} className="pix-book-cover" />
                        )}
                        <span className="pix-book-title">{book.title}</span>
                    </div>

                    <div className="pix-qr-wrapper">
                        <img src={qrCode} alt="QR Code PIX" className="pix-qr-img" />
                        <p className="pix-hint">
                            <Lock size={12} /> QR Code válido por 1 hora
                        </p>
                    </div>

                    <div className="pix-actions">
                        <button onClick={copyCode} className="btn btn-primary pix-btn">
                            <Clipboard size={16} />
                            {copiado ? "Copiado!" : "Copiar código Pix"}
                        </button>

                        <button
                            onClick={() => verifyPayment(true)}
                            disabled={loadingVerify}
                            className="btn btn-outline-success pix-btn"
                        >
                            {loadingVerify ? <Spinner size="16px" /> : <><CreditCard size={16} /> Já paguei</>}
                        </button>
                    </div>

                    <button onClick={cancelPix} className="pix-cancel">
                        <X size={14} /> Cancelar
                    </button>
                </div>
            </div>
        );
    }
    const coverSrc = `${BUCKET_READBOOKS_URL}/${book?.cover_url}` || "/img/book_transparent.png";

    // ── Tela de checkout ─────────────────────────────
    return (
        <div className="purchase-wrapper">
            <div className="purchase-card">
                {/* Cabeçalho com livro */}
                <div className="purchase-book-header">
                    <div className="purchase-book-cover-wrap">
                        {book.cover_url ? (
                            <img src={coverSrc} alt={book.title} className="purchase-book-cover" />
                        ) : (
                            <div className="purchase-book-placeholder">
                                <BookOpen size={32} />
                            </div>
                        )}
                    </div>
                    <div className="purchase-book-meta">
                        <h1 className="purchase-title">{book.title}</h1>
                        {book.author && <p className="purchase-author">{book.author}</p>}
                    </div>
                </div>

                <div className="purchase-divider" />

                {/* Método de pagamento */}
                <h5 className="purchase-section-title">Método de pagamento</h5>
                <div className="purchase-methods">
                    {/* <label
                        className={`purchase-method-item ${paymentMethod === "wallet" ? "selected" : ""} ${walletBalance < book.price ? "disabled" : ""}`}
                    >
                        <input
                            type="radio"
                            name="payMethod"
                            checked={paymentMethod === "wallet"}
                            disabled={walletBalance < book.price}
                            onChange={() => setPaymentMethod("wallet")}
                        />
                        <div className="method-info">
                            <span className="method-name">Saldo da Carteira</span>
                            <span className="method-desc">
                                {loadingWallet
                                    ? "Carregando..."
                                    : `Disponível: ${formatCurrency(walletBalance)}`}
                                {walletBalance < book.price && " · Saldo insuficiente"}
                            </span>
                        </div>
                    </label> */}

                    <label className={`purchase-method-item ${paymentMethod === "gerencianet_pix" ? "selected" : ""}`}>
                        <input
                            type="radio"
                            name="payMethod"
                            checked={paymentMethod === "gerencianet_pix"}
                            onChange={() => setPaymentMethod("gerencianet_pix")}
                        />
                        <div className="method-info">
                            <span className="method-name">PIX</span>
                            <span className="method-desc">Aprovação instantânea · QR Code gerado na hora</span>
                        </div>
                    </label>
                </div>

                {/* Resumo */}
                <div className="purchase-summary">
                    <div className="summary-row">
                        <span>{book.title}</span>
                        <span>{formatCurrency(book.price, book.currency)}</span>
                    </div>
                    <div className="summary-divider" />
                    <div className="summary-row total">
                        <span>Total</span>
                        <span>{formatCurrency(book.price, book.currency)}</span>
                    </div>
                </div>

                <button
                    onClick={submit}
                    disabled={loading}
                    className="btn btn-primary purchase-btn"
                >
                    {loading ? (
                        <Spinner size="20px" />
                    ) : paymentMethod === "gerencianet_pix" ? (
                        "Gerar PIX"
                    ) : (
                        "Comprar agora"
                    )}
                </button>

                <p className="purchase-secure">
                    <Lock size={12} /> Pagamento seguro
                </p>

                <button onClick={() => navigate(-1)} className="purchase-back">
                    Voltar
                </button>
            </div>
        </div>
    );
}